/**
 * SharedWithYouBanner — the strip a retailer / installer sees at the top of
 * a CHILD share link: who sent it, what they wrote, and how the page is priced.
 */

import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import type { EstimateViewer } from '../../types/share';
import './SharedWithYouBanner.css';

interface Props {
  viewer: EstimateViewer;
}

export const SharedWithYouBanner: React.FC<Props> = ({ viewer }) => {
  const { t } = useTranslation('results');
  const share = viewer.share;
  if (!share) return null;
  const sender = share.senderName || t('share.banner.someone');
  const pricing = viewer.pricing;

  let pricingLine: React.ReactNode = null;
  if (share.kind === 'retailer') {
    if (pricing.mode === 'retailer') {
      pricingLine = <Trans i18nKey="share.banner.pricedForYou" ns="results" values={{ retailer: pricing.retailerName ?? share.locationName }} components={{ b: <strong /> }} />;
    } else if (pricing.mode === 'msrp_fallback') {
      pricingLine = t('share.banner.pricedMsrp');
    } else if (pricing.mode === 'no_prices') {
      pricingLine = t('share.banner.quantitiesOnly');
    }
  } else {
    pricingLine = t('share.banner.pricedOriginal');
  }

  return (
    <div className="share-banner d-print-none" role="status">
      <div className="share-banner__icon" aria-hidden="true"><Send size={18} /></div>
      <div className="share-banner__body">
        <div className="share-banner__title">
          <Trans i18nKey="share.banner.title" ns="results" values={{ name: sender, location: share.locationName }} components={{ b: <strong /> }} />
        </div>
        {share.message.trim() !== '' && (
          <blockquote className="share-banner__message">{share.message}</blockquote>
        )}
        <div className="share-banner__meta">
          {pricingLine && <span>{pricingLine}</span>}
          <span>{viewer.role === 'promoted' ? t('share.banner.replyPromoted', { name: sender }) : t('share.banner.replyBelow', { name: sender })}</span>
        </div>
      </div>
    </div>
  );
};

export default SharedWithYouBanner;
